/**
 * Test failure extraction for cache functionality
 */

import { parsePlaywrightJson } from './parser';
import { ReportDiscoveryService } from './discovery';
import { FileSystemError, CLIOptions } from '@/types';
import { logger } from '@/utils/logger';

/**
 * Playwright test result structure
 */
interface PlaywrightTestResult {
  status: string;
  duration?: number;
  error?: {
    message?: string;
    name?: string;
  };
}

/**
 * Playwright test structure
 */
interface PlaywrightTest {
  title?: string;
  name?: string;
  status?: string;
  outcome?: string;
  state?: string;
  file?: string;
  location?: {
    file?: string;
  };
  duration?: number;
  error?: unknown;
  results?: PlaywrightTestResult[];
}

/**
 * Playwright suite structure
 */
interface PlaywrightSuite {
  title?: string;
  file?: string; // Suite can contain file path
  suites?: PlaywrightSuite[];
  specs?: PlaywrightSpec[];
  tests?: PlaywrightTest[];
}

/**
 * Playwright spec structure (intermediate level between suite and test)
 */
interface PlaywrightSpec {
  title?: string;
  ok?: boolean;
  tests?: PlaywrightTest[];
}

/**
 * Playwright report data structure
 */
interface PlaywrightReportData {
  suites?: PlaywrightSuite[];
  stats?: {
    expected?: number;
    unexpected?: number;
    skipped?: number;
    duration?: number;
  };
}

/**
 * Test failure data structure
 */
export interface TestFailure {
  file: string;
  testTitle: string;
  project?: string | undefined; // Browser/project name (chromium, firefox, etc.)
  error?: string | undefined;
  duration?: number | undefined;
}

/**
 * Test summary data structure
 */
export interface TestSummary {
  total: number;
  passed: number;
  failed: number;
  skipped: number;
  duration: number;
}

/**
 * Cache extraction result
 */
export interface CacheExtractionResult {
  failures: TestFailure[];
  summary: TestSummary;
  reportPaths: string[];
  hasData: boolean;
}

/**
 * Service for extracting test failure data for caching
 */
export class CacheExtractor {
  constructor(private readonly workingDir: string) {}

  /**
   * Extract test failures and metadata for caching
   */
  async extractFailureData(): Promise<CacheExtractionResult> {
    try {
      // Step 1: Discover JSON reports using existing discovery system
      const reportPaths = await this.discoverReports();

      if (reportPaths.length === 0) {
        return this.createEmptyResult();
      }

      // Step 2: Parse reports and extract failures
      const failures: TestFailure[] = [];
      const totalSummary: TestSummary = {
        total: 0,
        passed: 0,
        failed: 0,
        skipped: 0,
        duration: 0,
      };

      for (const reportPath of reportPaths) {
        try {
          const reportData = await parsePlaywrightJson(reportPath);

          // Extract failures from this report
          const reportFailures = this.extractFailuresFromReport(
            reportData,
            reportPath
          );
          failures.push(...reportFailures);

          // Aggregate summary data from stats
          if (reportData.stats) {
            const stats = reportData.stats;
            totalSummary.total +=
              (stats.expected || 0) +
              (stats.unexpected || 0) +
              (stats.skipped || 0);
            totalSummary.passed += stats.expected || 0;
            totalSummary.failed += stats.unexpected || 0;
            totalSummary.skipped += stats.skipped || 0;
            totalSummary.duration += Math.round(stats.duration || 0);
          }
        } catch (error) {
          // Log but continue with other reports
          logger.warn(
            `Failed to parse report ${reportPath}: ${error instanceof Error ? error.message : error}`
          );
          continue;
        }
      }

      return {
        failures,
        summary: totalSummary,
        reportPaths,
        hasData: reportPaths.length > 0,
      };
    } catch (error) {
      throw new FileSystemError(
        `Failed to extract test failure data: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Discover JSON reports using the existing discovery service
   */
  private async discoverReports(): Promise<string[]> {
    try {
      // Use the existing ReportDiscoveryService for comprehensive scanning
      const discoveryService = new ReportDiscoveryService(this.workingDir);

      // Create minimal options for discovery (only need JSON report)
      const options: CLIOptions = {
        reportDirectory: this.workingDir,
        token: '',
        uploadImages: false,
        uploadVideos: false,
        uploadHtml: false,
        uploadTraces: false,
        uploadFiles: false,
        uploadFullJson: false,
        verbose: false,
        environment: 'unknown',
        tags: [],
      };

      const discoveryResult = await discoveryService.discover(options);

      // Return the discovered JSON report path
      if (discoveryResult.jsonReport) {
        return [discoveryResult.jsonReport];
      }

      return [];
    } catch (error) {
      // If discovery fails, return empty array (no reports found)
      if (error instanceof FileSystemError) {
        console.debug('No JSON reports found:', error.message);
      }
      return [];
    }
  }

  /**
   * Extract failures from a parsed report
   */
  private extractFailuresFromReport(
    reportData: PlaywrightReportData,
    reportPath: string
  ): TestFailure[] {
    const failures: TestFailure[] = [];

    try {
      // Handle Playwright report structure - suites contain the test information
      const suites = reportData.suites || [];

      if (Array.isArray(suites)) {
        for (const suite of suites) {
          this.extractFailuresFromSuite(
            suite,
            failures,
            '',
            undefined,
            undefined
          );
        }
      }

      return failures;
    } catch (error) {
      logger.warn(
        `Failed to extract failures from ${reportPath}: ${error instanceof Error ? error.message : error}`
      );
      return [];
    }
  }

  /**
   * Recursively extract failures from suite structure
   */
  private extractFailuresFromSuite(
    suite: PlaywrightSuite,
    failures: TestFailure[],
    parentPath?: string,
    parentFile?: string,
    project?: string
  ): void {
    try {
      // Extract file from suite if available
      const suiteFile = suite.file || parentFile;

      // Detect project name: top-level suites without files are typically project names
      // (e.g., "chromium", "firefox", "webkit")
      const isProjectSuite = !suite.file && !parentFile && suite.title;
      const currentProject = isProjectSuite ? suite.title : project;

      // For building test path, only include non-file titles in the path
      // Don't include project names in the path
      const shouldIncludeInPath =
        suite.title &&
        (!suite.file || suite.title !== suite.file) &&
        !isProjectSuite;
      const suitePath = shouldIncludeInPath
        ? parentPath
          ? `${parentPath} > ${suite.title}`
          : suite.title
        : parentPath || '';

      // Handle nested suites (recursive)
      if (suite.suites && Array.isArray(suite.suites)) {
        for (const nestedSuite of suite.suites) {
          this.extractFailuresFromSuite(
            nestedSuite,
            failures,
            suitePath,
            suiteFile,
            currentProject
          );
        }
      }

      // Handle specs in this suite (Playwright report structure: suites -> specs -> tests)
      if (suite.specs && Array.isArray(suite.specs)) {
        for (const spec of suite.specs) {
          this.extractFailuresFromSpec(
            spec,
            failures,
            suitePath,
            suiteFile,
            currentProject
          );
        }
      }

      // Handle individual tests in this suite (fallback for different report structures)
      if (suite.tests && Array.isArray(suite.tests)) {
        for (const test of suite.tests) {
          this.extractFailuresFromTest(
            test,
            failures,
            suitePath,
            suiteFile,
            undefined,
            currentProject
          );
        }
      }
    } catch (error) {
      // Continue processing other suites on error
      logger.warn(
        `Error processing suite: ${error instanceof Error ? error.message : error}`
      );
    }
  }

  /**
   * Extract failures from spec structure (intermediate level between suite and test)
   */
  private extractFailuresFromSpec(
    spec: PlaywrightSpec,
    failures: TestFailure[],
    parentPath?: string,
    suiteFile?: string,
    project?: string
  ): void {
    try {
      // Use spec title as the test title directly (e.g., "get started link")
      const specTitle = spec.title || '';

      // Check if spec is marked as failed (spec.ok === false means test failed)
      // This is the reliable way to check for failures in Playwright reports
      const specFailed = spec.ok === false;

      // Handle individual tests in this spec
      if (spec.tests && Array.isArray(spec.tests) && specFailed) {
        // Only process the first test in the spec (all tests in a spec are the same test with retries)
        const test = spec.tests[0];
        if (test) {
          const failure = this.createTestFailure(
            test,
            parentPath,
            suiteFile,
            specTitle,
            project
          );
          if (failure) {
            // Check for duplicates (include project to differentiate same test in different browsers)
            const isDuplicate = failures.some(
              f =>
                f.file === failure.file &&
                f.testTitle === failure.testTitle &&
                f.project === failure.project
            );

            if (!isDuplicate) {
              failures.push(failure);
            }
          }
        }
      }
    } catch (error) {
      // Continue processing other specs on error
      logger.warn(
        `Error processing spec: ${error instanceof Error ? error.message : error}`
      );
    }
  }

  /**
   * Extract failures from individual test
   */
  private extractFailuresFromTest(
    test: PlaywrightTest,
    failures: TestFailure[],
    parentPath?: string,
    suiteFile?: string,
    specTitle?: string,
    project?: string
  ): void {
    try {
      // Handle direct test results - only count the test once, not each retry
      if (this.isFailedTest(test)) {
        const failure = this.createTestFailure(
          test,
          parentPath,
          suiteFile,
          specTitle,
          project
        );
        if (failure) {
          // Check if this failure already exists (avoid duplicates from retries)
          // Include project to differentiate same test in different browsers
          const isDuplicate = failures.some(
            f =>
              f.file === failure.file &&
              f.testTitle === failure.testTitle &&
              f.project === failure.project
          );

          if (!isDuplicate) {
            failures.push(failure);
          }
        }
      }
    } catch (error) {
      // Continue processing other tests on error
      logger.warn(
        `Error processing test: ${error instanceof Error ? error.message : error}`
      );
    }
  }

  /**
   * Check if a test item represents a failed test
   */
  private isFailedTest(item: PlaywrightTest): boolean {
    return Boolean(
      item &&
      (item.status === 'failed' ||
        item.outcome === 'failed' ||
        item.state === 'failed' ||
        item.results?.some(r => r.status === 'failed'))
    );
  }

  /**
   * Create TestFailure object from test data
   */
  private createTestFailure(
    test: PlaywrightTest,
    _parentPath?: string,
    suiteFile?: string,
    specTitle?: string,
    project?: string
  ): TestFailure | null {
    try {
      // Extract file path - prioritize test file, then suite file
      let filePath = test.file || test.location?.file || suiteFile || '';

      // Clean up file path (remove absolute path prefixes)
      if (filePath) {
        // Convert to relative path if possible
        const cwd = process.cwd();
        if (filePath.startsWith(cwd)) {
          filePath = filePath.substring(cwd.length + 1);
        }
      }

      // Extract test title - use spec title only (without suite path)
      // This ensures Playwright's -g flag can match exact test names
      const testTitle = specTitle || test.title || test.name || 'Unknown test';

      // Extract error information
      let error: string | undefined;
      if (test.results && Array.isArray(test.results)) {
        const failedResult = test.results.find(r => r.status === 'failed');
        if (failedResult?.error) {
          error = this.formatError(failedResult.error);
        }
      } else if (test.error) {
        error = this.formatError(test.error);
      }

      // Extract duration
      let duration: number | undefined;
      if (test.results && Array.isArray(test.results)) {
        duration = test.results.reduce(
          (total, r) => total + (r.duration || 0),
          0
        );
      } else if (typeof test.duration === 'number') {
        duration = test.duration;
      }

      // Only create failure if we have meaningful data
      if (!filePath && !testTitle) {
        return null;
      }

      return {
        file: filePath || 'unknown',
        testTitle: testTitle,
        project,
        error,
        duration,
      };
    } catch (error) {
      logger.warn(
        `Failed to create test failure object: ${error instanceof Error ? error.message : error}`
      );
      return null;
    }
  }

  /**
   * Format error message for storage
   */
  private formatError(error: unknown): string {
    try {
      if (typeof error === 'string') {
        return error;
      }

      if (error && typeof error === 'object') {
        const errorObj = error as Record<string, unknown>;
        return (
          (errorObj.message as string) ||
          (errorObj.name as string) ||
          JSON.stringify(error)
        );
      }

      return String(error);
    } catch {
      return 'Error formatting failed';
    }
  }

  /**
   * Create empty result for cases with no data
   */
  private createEmptyResult(): CacheExtractionResult {
    return {
      failures: [],
      summary: {
        total: 0,
        passed: 0,
        failed: 0,
        skipped: 0,
        duration: 0,
      },
      reportPaths: [],
      hasData: false,
    };
  }
}
